/* istanbul ignore file */
import React, { startTransition, useEffect, useMemo, useState } from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { Divider } from 'src/components/atoms/Divider/Divider';
import { ProgressBar } from 'src/components/atoms/ProgressBar/ProgressBar';
import { SkeletonProductList } from 'src/components/organisms/ProductList/components/SkeletonProductList';
import { ProductListContainer } from 'src/components/organisms/ProductList/containers/ProductListContainer';
import {
  NO_MATCHING_PRODUCTS_HEADING,
  NO_MATCHING_PRODUCTS_HINT,
} from 'src/components/organisms/ProductPages/constants/copy';
import { PAGINATION_WRAPPER_ID } from 'src/components/organisms/ProductPages/constants/elementIds';
import { PRODUCTS_GRID_WRAPPER } from 'src/constants/elementIds';
import type { UpdateHistoryPayload } from 'src/redux/products/thunks/updatePageHistory';
import type { Product } from 'src/redux/products/types/Product';
import { getPageNumbers } from 'src/redux/products/utils/pageNumbers';
import { getProductCountUpToEndOfPage } from 'src/redux/products/utils/productCount';
import { pageHistory } from 'src/services/pageHistory';
import {
  getProductCardNodeByCode,
  scrollProductCardIntoView,
} from 'src/utils/scrollProductCardIntoView';
import { PaginationButtonLink } from './PaginationButtonLink';
import * as styles from './ProductPages.styles';

const PRODUCTS_TO_RENDER_ON_SERVER = 12;

const splitProductsByIsExpandedSearch = (productsForThisPage: Product[]) => {
  return productsForThisPage.reduce(
    (result, product) => {
      result[!product.isExpandedSearch ? 0 : 1].push(product);
      return result;
    },
    [[], []] as [Product[], Product[]],
  );
};

type LastViewedProduct = Pick<UpdateHistoryPayload, 'productCode'>;

export interface ProductPagesProps {
  favouriteProductCodes: string[];
  /** Filters the customer can remove, so not the taxonomy a shop page is built on. */
  hasAppliedFilters: boolean;
  isLoading: boolean;
  loadMoreURL: string | null;
  loadPreviousURL: string | null;
  onLoadMore: ({ page }: { page: number }) => void;
  onLoadPrevious: ({ page }: { page: number }) => void;
  pageLoading: number | null;
  productsByPage: Record<number, Product[]>;
  totalProducts: number;
}

export const ProductPages = ({
  favouriteProductCodes,
  hasAppliedFilters,
  isLoading,
  loadMoreURL,
  loadPreviousURL,
  onLoadMore,
  onLoadPrevious,
  pageLoading,
  productsByPage,
  totalProducts,
}: ProductPagesProps) => {
  const [hasRenderedRemainingProducts, setHasRenderedRemainingProducts] = useState(false);
  const pageNumbers = getPageNumbers(productsByPage);
  /* Split once per set of pages rather than once per render. The two arrays are what
     the list below is keyed on all the way down, so rebuilding them on every render
     meant a favourite being toggled re-adapted and re-rendered every card on the page. */
  const productsSplitByPage = useMemo(
    () =>
      new Map(
        Object.entries(productsByPage).map(
          ([page, productsForThisPage]) =>
            [Number(page), splitProductsByIsExpandedSearch(productsForThisPage)] as const,
        ),
      ),
    [productsByPage],
  );
  const lastPageDisplayed = pageNumbers.slice(-1)[0];
  const loadingPreviousPage = pageLoading && pageLoading < pageNumbers[0];
  const loadingOnlyPage = pageLoading && pageNumbers.length === 0;
  const loadingNextPage = pageLoading && pageLoading > lastPageDisplayed;

  useEffect(() => {
    const product = pageHistory!.get<LastViewedProduct>('lastViewedProduct');

    if (product) {
      scrollProductCardIntoView(getProductCardNodeByCode(product.productCode));
    }
  }, []);

  /* Deliberately low priority. Nothing in this commit is above the fold, but it is
     48 of the page's 60 cards in one pass, and it grew when the card started carrying
     more than one image: each card now renders a slide, an image, an overlay link and
     a dot per image rather than one of each. Marking it a transition does not make the
     work smaller, it lets React yield part-way through so the browser can paint and
     stay responsive while it lands, instead of blocking on the whole commit. */
  useEffect(() => {
    if (!hasRenderedRemainingProducts) {
      startTransition(() => {
        setHasRenderedRemainingProducts(true);
      });
    }
  }, [hasRenderedRemainingProducts]);

  return (
    <div css={styles.wrapper} id={PRODUCTS_GRID_WRAPPER}>
      <h2 css={visuallyHidden}>Products</h2>
      {!isLoading && loadPreviousURL && (
        <PaginationButtonLink
          href={loadPreviousURL}
          onClick={() => {
            onLoadPrevious({ page: pageNumbers[0] });
          }}
        >
          Load previous<span css={visuallyHidden}> products</span>
        </PaginationButtonLink>
      )}
      {loadingPreviousPage && <SkeletonProductList />}
      {loadingOnlyPage ? (
        <SkeletonProductList />
      ) : totalProducts === 0 && hasAppliedFilters ? (
        // The applied-filter pills above this are the way out of it.
        <div css={styles.noMatchingProducts}>
          <p css={styles.noMatchingProductsHeading}>{NO_MATCHING_PRODUCTS_HEADING}</p>
          <p css={styles.noMatchingProductsHint}>{NO_MATCHING_PRODUCTS_HINT}</p>
        </div>
      ) : (
        pageNumbers.map((page) => {
          // Every page number came from the same record the map was built from.
          const [products, expandedProducts] = productsSplitByPage.get(page)!;
          const renderExpandedResultsMessage = !!expandedProducts?.length && page === 1;
          // eslint-disable-next-line no-extra-boolean-cast
          const expandedResultsMessage = !!products?.length ? (
            <Divider text="Similar items based on your search" />
          ) : (
            <h2 css={styles.expandedResultsMessage}>
              We couldn&apos;t find an exact match – but you might like these…
            </h2>
          );
          const progressCount = getProductCountUpToEndOfPage(productsByPage, page);
          const progressMessage = `Viewed ${progressCount} of ${totalProducts} products`;

          // For the first page, only render first 12 products on server, all on client
          const productsToRender =
            page === 1 && !hasRenderedRemainingProducts
              ? products.slice(0, PRODUCTS_TO_RENDER_ON_SERVER)
              : products;

          return (
            <div
              aria-hidden={isLoading}
              data-testid={`product-page-${page}`}
              key={`product-page-${page}`}
            >
              <ProductListContainer
                expandedProducts={expandedProducts}
                favouriteProductCodes={favouriteProductCodes}
                page={page}
                products={productsToRender}
                {...(renderExpandedResultsMessage && {
                  expandedResultsMessage,
                })}
              />
              <div css={[styles.progressWrapper, styles.fadeIn]} id={PAGINATION_WRAPPER_ID}>
                {lastPageDisplayed === page ? (
                  <>
                    <p css={styles.progressMessage}>{progressMessage}</p>
                    <ProgressBar part={progressCount} total={totalProducts} />
                  </>
                ) : (
                  <Divider text={progressMessage} />
                )}
              </div>
            </div>
          );
        })
      )}
      {loadingNextPage && <SkeletonProductList />}
      {!isLoading && loadMoreURL && (
        <PaginationButtonLink
          href={loadMoreURL}
          onClick={() => {
            onLoadMore({ page: lastPageDisplayed });
          }}
        >
          Load more<span css={visuallyHidden}> products</span>
        </PaginationButtonLink>
      )}
    </div>
  );
};
